import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Table } from 'react-bootstrap';
import XIcon from '../lib/XIcon';
import { XRPSCAN_API_URL } from '../../config';
import { handleResponse } from '../common/Helpers';
import LedgerTag from '../lib/LedgerTag';
import Skeleton from 'react-loading-skeleton';
import ValidatorTag from '../lib/ValidatorTag';
import EmptyStateNoRecrod from '../lib/EmptyStateNoRecord';
import XBadge from '../lib/XBadge';

const sortVotes = (data) => {
    return data.sort((a, b) => {
        const domainA = a.validator?.domain || a.validator?.domain_legacy || '';
        const domainB = b.validator?.domain || b.validator?.domain_legacy || '';
        return domainA.toLowerCase().localeCompare(domainB.toLowerCase());
    });
}

const AmendmentEnableVotes = (props) => {
    const { amendment } = props;
    const [ votes, setVotes ] = useState([]);
    const [ loading, setLoading ] = useState(false);

    const fetchVotes = useCallback((amendment_id) => {
        setLoading(true);
        fetch(`${XRPSCAN_API_URL}/amendment/${amendment_id}/votes`)
        .then(handleResponse)
        .then(data => {
            setVotes(sortVotes(data));
        })
        .catch(error => {
            setVotes([]);
        })
        .finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        fetchVotes(amendment.amendment_id);
    }, [amendment, fetchVotes]);

    return (
        <Card>
            <Card.Body>
                <Card.Title>
                    <XIcon icon='vote-yea' />
                    <span className="ml-2">{amendment.name || ""} amendment was voted in by</span>
                </Card.Title>
                <Row>
                    <Col xs={12} md={12}>

                    {/* Loading state */}
                    {loading &&
                    <Table responsive>
                        <tbody>
                            <tr><td className="border-top-0"><Skeleton /></td></tr>
                            <tr><td className="border-top-0"><Skeleton /></td></tr>
                        </tbody>
                    </Table>
                    }

                    {/* Loaded state, but no records available */}
                    {!loading && votes?.length === 0 &&
                        <div className='mt-2'>
                            <EmptyStateNoRecrod title="Voting data unavailable">
                                Voting data for older amendments are not available.
                            </EmptyStateNoRecrod>
                        </div>
                    }

                    {/* Loaded state, recrods available */}
                    {votes?.length > 0 &&
                        <Table responsive>
                            <thead>
                                <tr className='text-nowrap'>
                                    <th className="text-left">#</th>
                                    <th className="text-left">Vote</th>
                                    <th className="text-left">Validator</th>
                                    <th className="text-center">Server version</th>
                                    <th className="text-center">Enabled in ledger</th>
                                </tr>
                            </thead>
                            <tbody>
                            {votes?.length >= 0 && votes.map((vote, index) =>
                            <tr key={index} className='text-nowrap'>
                                <td>
                                    {index+1}
                                </td>
                                <td>
                                    {vote?.amendments?.includes(amendment.amendment_id) ?
                                    <>
                                        <XIcon icon="vote-yea" className="text-success" size="lg"/>
                                    </>
                                    :
                                    <>
                                        <XIcon icon="vote-nay" className="text-danger" size="lg"/>
                                    </>
                                    }
                                </td>
                                <td>
                                    <span className="text-monospace">
                                        {vote?.validator ?
                                            <ValidatorTag validator={vote?.validator} verificationIcon={false}>{vote?.validator?.master_key}</ValidatorTag>
                                        :
                                            vote?.master_key
                                        }
                                    </span>
                                </td>
                                <td className='text-center'>
                                    {vote?._server_version?.version &&
                                        <XBadge variant="primary" className="text-monospace">{vote?._server_version?.version}</XBadge>
                                    }
                                </td>
                                <td className="text-center">
                                    <LedgerTag ledger_index={vote?.ledger_index} />
                                </td>
                            </tr>
                            )}
                            </tbody>
                        </Table>
                        }
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

AmendmentEnableVotes.propTypes = {
    amendment: PropTypes.object.isRequired,
}

export default AmendmentEnableVotes;